import { render, html } from "lit";
import { ColorTypes, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, ColumnSource, RequestFilters, RowSource, renderers } from "@omegagrid/grid";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { Accounting, AccountingGroup, AccountingSettings } from "@reporting/rp-types/core";
import { AccountingTreeAdapter, AccountingGroupTreeOptions, AvailableAccountingTreeAdapter } from "..";
import { synchronizeServers, type SyncTable } from "../utils/serverUtils";

const PAGE_SIZE = 500;

/*
* This is the accounting group grid editor adapter only for SUPER administration!
*/

export class AccountingGroupGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	/** Number of rows already returned by the endpoint, used as the offset of the next page. */
	private _loadedRows = 0;

	constructor(
		private params: AccountingGroupTreeOptions,
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 6,
			filter: false,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}]
		}, {
			data: '_id',
			header: '#',
			t: 'n',
			z: '0',
			e: false,
			w: 80,
		}, {
			data: '_name',
			header: msg('Name'),
			t: 't',
			e: true,
			w: 240,
			levelButtons: true,
			levelIndent: true,
		}, {
			data: 'accounting_parent',
			header: msg('Parent'),
			t: 's',
			e: true,
			w: 150,
		}, {
			data: 'accounting_system',
			header: msg('System'),
			t: 't',
			e: true,
			w: 150,
		}, {
			data: 'hidden',
			header: msg('Hidden'),
			t: 'b',
			e: true,
			w: 80,
		}, {
			data: 'fin_flag',
			header: `${msg('Finance')} (F)`,
			t: 'b',
			e: true,
			w: 120,
		}, {
			data: 'item_flag',
			header: `${msg('Items')} (I)`,
			t: 'b',
			e: true,
			w: 120,
		}, {
			data: 'balance_flag',
			header: `${msg('Aging')} (A)`,
			t: 'b',
			e: true,
			w: 120,
		}, {
			data: 'has_uid',
			header: msg('API'),
			t: 'b',
			e: false,
			renderer: this.apiRenderer,
			w: 140,
		}, {
			data: 'host',
			header: msg('Server'),
			t: 't',
			e: false,
			w: 180,
		}, {
			data: 'date_activated',
			header: msg('Date activated'),
			t: 'dt',
			e: false,
			w: 145,
		}];
		if (true) { // TODO: isHelios?
			this._columns.push({
				data: 'helios_sn',
				header: msg('Helios SN'),
				t: 't',
				e: false,
				w: 120,
				l: 0,
				collapsed: true
			}, {
				data: 'helios_licence_count',
				header: msg('Helios Licence Count'),
				t: 'n',
				e: false,
				w: 170,
				l: 1
			}, {
				data: 'helios_guid',
				header: msg('Helios GUID'),
				t: 't',
				e: false,
				w: 120,
				l: 1
			}, {
				data: 'helios_version',
				header: msg('Helios Version'),
				t: 't',
				e: false,
				w: 140,
				l: 1
			}, {
				data: 'helios_params',
				header: msg('Helios Params'),
				t: 't',
				e: false,
				w: 140,
				l: 1
			});
		}
		this._columns.push({
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}, {
			data: 'accounting',
			t: 'n',
			e: false,
			hidden: true,
		}, {
			data: 'id_group',
			t: 'n',
			e: true,
			hidden: true,
		}, {
			data: 'id_server',
			t: 'n',
			e: true,
			hidden: true,
		}, {
			data: '_type',
			t: 't',
			e: true,
			hidden: true,
		});
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const rows: RowSource[] = [];
		const params = this.createRequestParams(filters,
			{ count: PAGE_SIZE },
			{ _id: 'accounting', _name: 'company' },
		);
		// the endpoint pages by whole groups, so the offset has to count the rows it returned; the grid
		// counts its own rows instead, including the ones hidden under a collapsed group and unsaved new ones
		if (!filters?.offset) this._loadedRows = 0; // first page or reload after refresh/filter/sort
		params.offset = this._loadedRows;
		const accountings = await rp.ms('accounting').get<AccountingSettings[]>('groupTree', Object.assign(params, this.params));
		this._loadedRows += accountings.length;
		for (const a of accountings) {
			if (!a.accounting) { // group header row
				rows.push({ l: 0, data: {
					controls: { content: this.getRowControls(true) },
					_id: a.id_group,
					_name: a.group_name,
					accounting_parent: { t: 't', e: false, cs: 7, dv: this.getGroupDisplayValue(a) },
					date_activated: a.group_date_activated,
					accounting: null,
					id_group: a.id_group,
					host: a.host, // the server the whole group is hosted on
					_type: 'g'
				}});
				continue;
			}
			rows.push({ l: a.level + 1, data: {
					...a,
					controls: { content: this.getRowControls(false) },
					_id: a.accounting,
					_name: a.company,
					accounting_parent: { adapter: new AccountingTreeAdapter({ idGroup: a.id_group, excludedAccounting: a.accounting }), v: a.accounting_parent?.toString(), dv: a.company_parent },
					has_uid: !!a.has_uid,
					_type: 'a'
				},
			});
		}
		return rows;
	}

	getGroupDisplayValue(a: AccountingSettings): string {
		let dv = `${msg('accountings')}: ${a.accounting_count ?? 0}`;
		dv += `, ${msg('users')}: ${a.user_count ?? 0}`;
		dv += `, ${msg('max users')}: ${a.max_user_count ?? '∞'}`;
		dv += `, ${msg('max logins')}: ${a.login_limit ?? '∞'}`;
		if (a.group_disable_ai) dv += `, ${msg('AI disabled')}`;
		return dv;
	}

	getRowControls = (isGroup: boolean) => {
		return isGroup ? [
			{ action: 'edit', title: msg('Edit'), icon: 'pen', color: ColorTypes.Gray },
			{ action: 'settings', title: msg('Settings'), icon: 'gear', color: ColorTypes.Gray },
			{ action: 'access', title: msg('Access'), icon: 'key', color: ColorTypes.Yellow },
			{ action: 'newAccounting', title: msg('Add'), icon: 'plus', color: ColorTypes.Blue },
			{ action: 'users', title: msg('Users'), icon: 'user', color: ColorTypes.Green },
			{ action: 'userGroups', title: msg('User groups'), icon: 'users', color: ColorTypes.Green }
		] : [
			{ action: 'database', title: msg('Database'), icon: 'database', color: ColorTypes.Yellow },
			{ action: 'settings', title: msg('Settings'), icon: 'gear', color: ColorTypes.Gray },
			{ action: 'access', title: msg('Access'), icon: 'key', color: ColorTypes.Yellow },
			{ action: 'newAccounting', title: msg('Add'), icon: 'plus', color: ColorTypes.Blue },
			{ action: 'duplicateAccounting', title: msg('Duplicate'), icon: 'copy', color: ColorTypes.Accent },
			{ action: 'tables', title: msg('Tables'), icon: 'chart-bar', color: ColorTypes.Red }
		];
	}

	apiRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);
		if (cell?.row?.data?._type !== 'a') return div;

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		render(!cell.value ? html`
				<og-button
					icon="cloud-plus"
					color="blue"
					style="height: 18px; width: 100%;"
					text="${msg('Create key')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('createApiKey', cell)}">
				</og-button>
			` : html`
				<og-button
					icon="copy"
					color="yellow"
					style="height: 18px; width: 100%;"
					text="${msg('Copy key')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('copyApiKey', cell)}">
				</og-button>
			`
		, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: true,
			filters: true,
			columnTree: false,
			columnHeaderTreeButtons: true,
			rowTree: false,
			levelButtonCell: { r: -2, c: 0 },
			refreshAfterSave: true,
			pageSize: PAGE_SIZE,
			toolbar: {
				new: {enabled: false},
			},
			customToolbar: [{
				type: 'button',
				id: 'newGroup',
				text: msg('New Group'),
				icon: 'plus',
				color: ColorTypes.Blue,
			}, {
				type: 'button',
				id: 'sections',
				text: msg('Sections'),
				icon: 'list-ol',
				color: ColorTypes.Gray,
			}] as ToolbarItem[],
		});
	}

	async save(rowData: RowData[]) {
		const groups: AccountingGroup[] = [];
		const accountings: (Accounting & { _newAccounting?: number })[] = [];
		// the tables new rows go into - only those are synchronized to the other servers afterwards
		const syncTables = new Set<SyncTable>();
		let missingIdConfirmed = false;
		for (const r of rowData) {
			const data = r.data;
			// deleting a group or a company is not supported here - without this the row would be
			// posted as an ordinary edit and simply come back on the next refresh
			if (r.state === 'deleted') continue;
			if (data._type === 'g') {
				groups.push({
					id_group: data._id as number,
					group_name: data._name as string || '',
				});
				if (r.state === 'new') syncTables.add('P_ACCOUNTING_GROUP');
			} else if (data._type === 'a' && !isNaN(data.id_group as number)) {
				if (!data._id && !missingIdConfirmed) {
					// only 'Local' sections (and a section with no type) are handed out automatically,
					// so an exhausted or 'Group'-only set of sections cannot be resolved for the user
					const available = await rp.ms('accounting').get<{ props?: { type?: string } }[]>(`availableAccountings/${data.id_group}`);
					if (!available?.some(i => i.props && (i.props.type === 'Local' || i.props.type === ''))) {
						rp.host.showAlert({
							content: `${msg('No accounting ID can be assigned automatically.')} ${msg('Add or extend a section of this group.')}`,
							type: 'danger',
							title: msg('Companies')
						});
						return Promise.reject('no available accounting ID');
					}
					const command = await rp.host.openMenu({
						content: 'yes_no',
						text: `${msg('Accounting ID is missing.')} ${msg('Do you want to assign all missing IDs automatically (local numbers only)?')}`
					});
					if (command?.id != 'Y') return Promise.reject('user cancelled');
					missingIdConfirmed = true;
				}
				accountings.push({
					_newAccounting: r.state === 'new' ? data._id as number : null,
					accounting: r.state !== 'new' ? data._id as number : null,
					company: data._name as string || '',
					id_accounting_group: data.id_group as number,
					accounting_parent: data.accounting_parent as number,
					accounting_system: data.accounting_system as string,
					hidden: data.hidden as boolean,
					fin_flag: data.fin_flag as boolean,
					item_flag: data.item_flag as boolean,
					balance_flag: data.balance_flag as boolean,
				});
				if (r.state === 'new') syncTables.add('P_ACCOUNTING');
			}
		}
		if (groups.length) await rp.ms('accounting').post('groups', groups);
		let saved: Accounting[] = [];
		if (accountings.length) saved = await rp.ms('accounting').post<Accounting[]>('accountings', accountings) ?? [];
		if (syncTables.size) {
			const currentServer = rp.accountingProvider.accounting?.id_server;
			const crossServer = saved.some((a, i) => accountings[i].accounting == null && a.id_server != null && a.id_server !== currentServer);
			const synchronization = synchronizeServers(Array.from(syncTables));
			if (crossServer) await synchronization;
		}
	}

	createNewRowData(data: any, duplicateRow: boolean = false) {
		let newRowData: Record<string, any> = {};

		if (duplicateRow) { // copy all editable fields
			this.editor.grid.model.columns.forEach(column => {
				if (column.editable) newRowData[column.data] = data[column.data];
			});
		}

		newRowData.accounting_parent = { adapter: new AccountingTreeAdapter({ idGroup: data.id_group }) };
		if (data._type === 'a') { // set values if the action is raised from the accounting row
			newRowData.accounting_parent.v = duplicateRow ? (typeof data.accounting_parent === 'string' ? data.accounting_parent : null) : (data._id?.toString() ?? null);
			newRowData.accounting_parent.dv = duplicateRow ? (data.company_parent ?? null) : (data._name ?? null);
		}
		newRowData._id = {
			t: 's',
			e: true,
			// validation: { allowInvalid: true },
			adapter: new AvailableAccountingTreeAdapter(data.id_group),
		};
		newRowData.id_group = data.id_group;
		newRowData._type = 'a';

		return newRowData;
	}

	async onAction(action: string, cell: CellModel) {
		const data = cell?.row.data;
		const a = data?.accounting as number;
		switch (action) {
			// toolbar
			case 'newGroup':
				this.editor.newRow(0).data = {
					accounting_parent: { t: 't', e: false, cs: 7 },
					_type: 'g',
				};
				this.editor.dispatchEditEvent();
				break;
			case 'sections':
				rp.host.open('accounting-sections', { title: msg('Sections') });
				break;
			// row actions
			case 'newAccounting':
				this.editor.newRow(cell.row.index + 1).data = this.createNewRowData(data);
				this.editor.dispatchEditEvent();
				break;
			case 'duplicateAccounting':
				this.editor.newRow(cell.row.index + 1).data = this.createNewRowData(data, true);
				this.editor.dispatchEditEvent();
				break;
			case 'edit':
				rp.host.openModalSidebar({id: `accounting-groupForm-${data.id_group}`, caption: `${msg('Edit')} ${data._name}`});
				break;
			case 'settings':
				// group row -> group-scope settings; accounting row -> accounting-scope settings
				rp.host.open(
					data._type === 'g'
						? `users-settings-group-${data.id_group}`
						: `users-settings-accounting-${data.accounting}`,
					{ title: `${msg('Settings')}: ${data._name}` }
				);
				break;
			case 'users':
				rp.host.open(`users-users-super-${data.id_group}`, { title: `${msg('Users')}: ${data._name}` });
				break;
			case 'userGroups':
				rp.host.open(`users-userGroups-${data.id_group}`, { title: `${msg('User groups')}: ${data._name}` });
				break;
			case 'database':
				rp.host.open(`accounting-dbSettings-${data._id}`, { title: `${msg('Database')}: ${data._name}` });
				break;
			case 'access':
				rp.host.open(
					`users-userAccessContainer-g-${data.id_group}${data._type === 'a' ? `-a-${data.accounting}` : ''}`,
					{ title: `${msg('Access')}: ${data._name}` }
				);
				break;
			case 'tables':
				rp.host.open(
					`super-tables-${data.id_server}`,
					{ title: `${msg('Tables')}: ${data._name}` }
				);
				break;
			case 'createApiKey': // apiRenderer
				if (a) {
					const command = await rp.host.openMenu({
						content: 'yes_no',
						text: msg('Are you sure?')
					});
					if (command?.id == 'Y') await rp.ms('accounting').post('createUID', null, { accounting: a }).then(() => this.editor.refresh());
				}
				break;
			case 'copyApiKey': // apiRenderer
				if (a) {
					const uid = await rp.ms('accounting').get<{ uid: string }>('uid', { accounting: a });
					await navigator.clipboard.writeText(uid?.uid ?? '');
					rp.host.showAlert({ content: msg('Copied to clipboard'), type: 'info', title: msg('API') });
				}
				break;
			case 'removeBtn':
				this.editor.setCellValue(cell.row.index, cell.column.index, null);
				break;
		}
	}

}
